import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { peek, put, storedLocation } from "../data/routeData";
import { peopleKey, loadPeople } from "../data/routeLoaders";
import { useSearchParams, useNavigate } from "react-router-dom";
import PageShell from "../components/chrome/PageShell";
import PeopleYouMayKnow, {
  readDismissedPeople,
} from "../components/PeopleYouMayKnow";
import FriendsDirectory from "../components/FriendsDirectory";
import FriendEventFilter from "../components/FriendEventFilter";
import RouteLoadError from "../components/transitions/RouteLoadError";
import useScrollRestore from "../hooks/useScrollRestore";
import MobileEventSheet from "../components/MobileEventSheet";
import useMediaQuery from "../utils/useMediaQuery";
import friendService from "../services/friendService";
import storyService from "../services/storyService";
import { signalService } from "../services";
import PortalRing from "../components/stories/PortalRing";
import StoryViewer from "../components/stories/StoryViewer";
import PeoplePortalCard from "../components/people/PeoplePortalCard";
import PeopleMasonry from "../components/people/PeopleMasonry";
import {
  SectionHeading,
  usePeopleToast,
  errorMessage,
} from "../components/people/PeopleUI";
import LoadingSpinner from "../components/LoadingSpinner";
import ProsTab from "../components/people/ProsTab";
import InviteFriendsSheet, {
  useInviteLink,
  copyText,
} from "../components/people/InviteFriendsSheet";

export default function PeoplePage() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const tab = ["events", "suggested", "friends", "pros"].includes(params.get("tab"))
    ? params.get("tab")
    : "events";
  const setTab = (key) =>
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("tab", key);
      next.delete("event");
      next.delete("reel");
      return next;
    });
  useScrollRestore("people|" + tab);
  const mobile = useMediaQuery("(max-width: 767px)");
  const cached = peek(peopleKey());
  const [signals, setSignals] = useState(cached?.signals || []);
  const [activity, setActivity] = useState(cached?.activity || []);
  const [loading, setLoading] = useState(!cached);
  const [loadError, setLoadError] = useState(null);
  const [friends, setFriends] = useState();
  const [suggestions, setSuggestions] = useState();
  const [extrasLoading, setExtrasLoading] = useState(true);
  const [friendCount, setFriendCount] = useState(null);
  const [suggestedCount, setSuggestedCount] = useState(null);
  // Filled in once the PROS tab has asked the directory; nothing is printed before that.
  const [prosCount, setProsCount] = useState(null);
  const [friendFilter, setFriendFilter] = useState(new Set());
  const [leaving, setLeaving] = useState(new Set());
  const dismissing = useRef(new Set());
  const timers = useRef(new Set());
  const [notify, toast] = usePeopleToast();
  const [reel, setReel] = useState(null);
  const [doorRect, setDoorRect] = useState(null);
  // Keys the viewer, bumped on every tap — a tap always opens a fresh one, even when the
  // parameter already names that person.
  const [reelTap, setReelTap] = useState(0);
  const [now, setNow] = useState(Date.now());
  const [headerHeight, setHeaderHeight] = useState(mobile ? 108 : 72);
  useEffect(() => {
    const header =
      document.querySelector("[data-pr-chrome]") ||
      document.querySelector("[data-topbar]");
    if (!header) return;
    const measure = () =>
      setHeaderHeight(header.getBoundingClientRect().height);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(header);
    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 30000);
    const active = timers.current;
    return () => {
      clearInterval(timer);
      active.forEach(clearTimeout);
    };
  }, []);
  const load = useCallback(async () => {
    try {
      setLoadError(null);
      const data = await loadPeople();
      setSignals(data.signals);
      setActivity(data.activity);
    } catch (e) {
      setLoadError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    load();
  }, [load]);
  useEffect(() => {
    let cancelled = false;
    Promise.allSettled([
      friendService.getFriends(),
      friendService.getSuggestedFriends(12),
    ]).then(([f, s]) => {
      if (cancelled) return;
      if (f.status === "fulfilled" && f.value?.success) {
        setFriends(f.value.data || []);
        setFriendCount((f.value.data || []).length);
      }
      if (s.status === "fulfilled" && s.value?.success) {
        const people = s.value.data || [];
        setSuggestions(people);
        const hidden = readDismissedPeople();
        setSuggestedCount(people.filter((p) => !hidden.has(p.userId)).length);
      }
      setExtrasLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, []);
  const handleEventOpen = useCallback(
    (event) => {
      if (!event?.inviteCode) return;
      if (!mobile) {
        navigate("/events/" + event.inviteCode);
        return;
      }
      setParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("event", event.inviteCode);
        return next;
      });
    },
    [mobile, navigate, setParams]
  );
  const closeOverlay = useCallback(
    (key) => {
      if ((window.history.state?.idx || 0) > 0) {
        navigate(-1);
        return;
      }
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete(key);
          return next;
        },
        { replace: true }
      );
    },
    [navigate, setParams]
  );
  const viewPortal = useCallback(
    async (userId, event, fallbackId) => {
      const rect =
        event?.portalRingRect || event?.currentTarget?.getBoundingClientRect();
      setDoorRect(
        rect
          ? {
              left: rect.left,
              top: rect.top,
              width: rect.width,
              height: rect.height,
            }
          : null
      );
      try {
        const result = await storyService.getReel(userId, storedLocation());
        if (result.success && result.data?.portal) {
          setReel(result.data);
          setReelTap((n) => n + 1);
          setParams((prev) => {
            const next = new URLSearchParams(prev);
            next.set("reel", userId);
            return next;
          });
        } else if (fallbackId) navigate("/portals/" + fallbackId);
        else notify("No live portal available right now.");
      } catch {
        notify("Could not open this portal. Try again.");
      }
    },
    [navigate, notify, setParams]
  );
  // Direct links to a reel are also resolvable, not only clicks during this mount.
  useEffect(() => {
    const id = params.get("reel");
    if (!id || reel?.authorUserId === id) return;
    let cancelled = false;
    storyService
      .getReel(id, storedLocation())
      .then((r) => {
        if (!cancelled && r.success) setReel(r.data);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [params, reel?.authorUserId]);
  const live = useMemo(
    () =>
      signals.filter(
        (s) => !s.expiresAtUtc || new Date(s.expiresAtUtc).getTime() > now
      ),
    [signals, now]
  );
  const eventFriends = useMemo(() => {
    const map = new Map();
    for (const a of activity) {
      if (a.type !== "you_going" && a.actorUserId)
        map.set(a.actorUserId, {
          userId: a.actorUserId,
          name: a.actorName,
          avatarUrl: a.actorAvatarUrl,
          livePortalCount: 0,
        });
      for (const f of a.interestedFriends || [])
        if (!map.has(f.userId)) map.set(f.userId, { ...f, livePortalCount: 0 });
    }
    for (const s of live) {
      if (s.isMine) continue;
      const friend = map.get(s.hostUserId) || {
        userId: s.hostUserId,
        name: s.hostDisplayName,
        avatarUrl: s.hostAvatarUrl,
        livePortalCount: 0,
      };
      friend.livePortalCount++;
      map.set(s.hostUserId, friend);
    }
    return [...map.values()];
  }, [activity, live]);
  const filtered = useMemo(
    () =>
      friendFilter.size
        ? activity.filter(
            (a) =>
              (a.type !== "you_going" && friendFilter.has(a.actorUserId)) ||
              (a.interestedFriends || []).some((f) =>
                friendFilter.has(f.userId)
              )
          )
        : activity,
    [activity, friendFilter]
  );
  const interested = useMemo(
    () => filtered.filter((a) => a.type === "friend_interested"),
    [filtered]
  );
  const otherActivity = useMemo(
    () => filtered.filter((a) => a.type !== "friend_interested"),
    [filtered]
  );
  const shownSignals = useMemo(
    () =>
      friendFilter.size
        ? live.filter((s) => friendFilter.has(s.hostUserId))
        : live,
    [live, friendFilter]
  );
  const toggleFriend = (id) =>
    setFriendFilter((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const update = (data) => {
    if (!data?.eventId) return;
    setSignals((prev) => {
      const next = prev.map((s) =>
        s.eventId === data.eventId ? { ...s, ...data } : s
      );
      put(peopleKey(), { signals: next, activity });
      return next;
    });
  };
  const dismiss = async (signal) => {
    if (dismissing.current.has(signal.eventId)) return;
    dismissing.current.add(signal.eventId);
    setLeaving((prev) => new Set(prev).add(signal.eventId));
    try {
      const result = await signalService.dismiss(signal.eventId);
      if (!result.success)
        throw new Error(errorMessage(result, "Could not hide this portal."));
      // Keep back-navigation correct even if this page unmounts during the fade.
      put(peopleKey(), {
        signals: (peek(peopleKey())?.signals || signals).filter(s => s.eventId !== signal.eventId),
        activity,
      });
      const timer = setTimeout(() => {
        timers.current.delete(timer);
        setSignals((prev) => {
          const next = prev.filter((s) => s.eventId !== signal.eventId);
          put(peopleKey(), { signals: next, activity });
          return next;
        });
        setLeaving((prev) => {
          const next = new Set(prev);
          next.delete(signal.eventId);
          return next;
        });
        dismissing.current.delete(signal.eventId);
        notify("Portal hidden", async () => {
          try {
            const r = await signalService.undismiss(signal.eventId);
            if (!r.success)
              throw new Error(
                errorMessage(r, "Could not restore this portal.")
              );
            setSignals((prev) => {
              const next = [
                signal,
                ...prev.filter((s) => s.eventId !== signal.eventId),
              ];
              put(peopleKey(), { signals: next, activity });
              return next;
            });
          } catch (e) {
            notify(e.message);
          }
        });
      }, 220);
      timers.current.add(timer);
    } catch (e) {
      dismissing.current.delete(signal.eventId);
      setLeaving((prev) => {
        const next = new Set(prev);
        next.delete(signal.eventId);
        return next;
      });
      notify(e.message);
    }
  };
  const updates =
    activity.filter((a) => now - new Date(a.occurredAt).getTime() < 86400000)
      .length + live.length;
  const counts = {
    events: activity.length + live.length,
    suggested: suggestedCount,
    friends: friendCount,
    pros: prosCount,
  };
  const location = storedLocation();
  // INVITE FRIENDS: the link is fetched ahead so the tap can copy it while the gesture is
  // still live (a clipboard write after an await is refused on iOS). The sheet copies it
  // itself when the fetch hasn't landed yet.
  const inviteLink = useInviteLink();
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteCopied, setInviteCopied] = useState(false);
  const openInvite = () => {
    if (inviteLink?.url) {
      copyText(inviteLink.url).then((ok) => setInviteCopied(ok));
      setInviteCopied(true);
    } else {
      setInviteCopied(false);
    }
    setInviteOpen(true);
  };
  const filterKey = [...friendFilter].sort().join(",");
  return (
    <PageShell bare active="people">
      <InviteFriendsSheet
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        copiedAtStart={inviteCopied}
        link={inviteLink}
      />
      <div
        className="people-page"
        style={{ "--people-header-height": headerHeight + "px" }}
      >
        <section className="people-intro">
          <div className="people-container people-intro-inner">
            <div>
              <div className="people-kicker">
                YOUR PEOPLE
                {location.label
                  ? " · " + location.label.split(",")[0].toUpperCase()
                  : ""}
              </div>
              <h1>PEOPLE</h1>
              <p>What your people are doing.</p>
            </div>
            <div className="people-counts">
              <button
                type="button"
                className="people-live-count"
                onClick={() => setTab("events")}
              >
                <span className="people-mini-ring">
                  <PortalRing
                    size={32}
                    state="live"
                    avatarUrl={live[0]?.hostAvatarUrl}
                    initial={(live[0]?.hostDisplayName || "P")[0]}
                  />
                </span>
                <strong>{live.length}</strong>PORTALS LIVE{" "}
                <span style={{ color: "cyan" }}>→</span>
              </button>
              <button
                type="button"
                className="people-invite-count"
                onClick={openInvite}
                data-testid="invite-friends-button"
              >
                <span className="people-invite-plus" aria-hidden>
                  +
                </span>
                INVITE FRIENDS
              </button>
              <button
                type="button"
                className="people-secondary-count"
                onClick={() => setTab("friends")}
              >
                <strong>{friendCount ?? "…"}</strong>FRIENDS
              </button>
              <button
                type="button"
                className="people-secondary-count"
                onClick={() => setTab("suggested")}
              >
                <strong>{suggestedCount ?? "…"}</strong>SUGGESTED
              </button>
            </div>
          </div>
        </section>
        <nav className="people-tabs" aria-label="People sections">
          <div className="people-container people-tabs-inner" data-tabs="4">
            {["events", "suggested", "friends", "pros"].map((key) => (
              <button
                type="button"
                key={key}
                aria-pressed={tab === key}
                onClick={() => setTab(key)}
              >
                {key}
                {(key !== "pros" || counts.pros != null) && (
                  <span>{counts[key] ?? "…"}</span>
                )}
              </button>
            ))}
            <div className="people-updates">{updates} updates today</div>
          </div>
        </nav>
        <main className="people-container people-content">
          {tab === "events" && (
            <>
              <RouteLoadError error={loadError} onRetry={load} />
              <FriendEventFilter
                friends={eventFriends}
                selected={friendFilter}
                onToggle={toggleFriend}
                onClear={() => setFriendFilter(new Set())}
              />
              <SectionHeading
                detail={
                  <span className="people-live-label">
                    <i className="people-dot" />
                    {shownSignals.length} live
                  </span>
                }
              >
                Portals
              </SectionHeading>
              {loading ? (
                <LoadingSpinner label="Loading your people…" />
              ) : !shownSignals.length ? (
                <p className="people-empty">
                  No live portals
                  {friendFilter.size
                    ? " from the friends you picked"
                    : " right now"}
                  .
                </p>
              ) : (
                <div className="people-portals">
                  {shownSignals.map((s) => (
                    <PeoplePortalCard
                      key={s.eventId}
                      signal={s}
                      onUpdate={update}
                      onDismiss={dismiss}
                      leaving={leaving.has(s.eventId)}
                      notify={notify}
                      onViewPortal={viewPortal}
                      onEventOpen={handleEventOpen}
                    />
                  ))}
                </div>
              )}
              <SectionHeading detail="Where your people are looking">
                Interested
              </SectionHeading>
              {!loading && !interested.length && (
                <p className="people-empty">
                  Quiet for now. Your friends' saved events will show up here.
                </p>
              )}
              <PeopleMasonry
                activity={interested}
                onOpen={handleEventOpen}
                filterKey={filterKey}
              />
              {otherActivity.length > 0 && (
                <section style={{ marginTop: 32 }}>
                  <SectionHeading detail="Plans taking shape">
                    More from your people
                  </SectionHeading>
                  <PeopleMasonry
                    activity={otherActivity}
                    onOpen={handleEventOpen}
                    filterKey={filterKey}
                  />
                </section>
              )}
            </>
          )}
          {/* Keep the loaded directories mounted across tabs so pending requests and undo survive. */}
          <div hidden={tab !== "suggested"}>
            {extrasLoading ? (
              tab === "suggested" && <LoadingSpinner label="Finding people…" />
            ) : (
              <PeopleYouMayKnow
                initialPeople={suggestions}
                onCount={setSuggestedCount}
              />
            )}
          </div>
          <div hidden={tab !== "friends"}>
            {extrasLoading ? (
              tab === "friends" && <LoadingSpinner label="Loading friends…" />
            ) : (
              <FriendsDirectory
                initialFriends={friends}
                onCount={setFriendCount}
                onViewPortal={viewPortal}
              />
            )}
          </div>
          {/* PROS (round-3 handoff §3) — the directory. Its filters live in the URL, so it
              mounts only while it is the tab on screen and loses nothing on a switch. */}
          {tab === "pros" && (
            <ProsTab
              params={params}
              setParams={setParams}
              notify={notify}
              onCount={setProsCount}
            />
          )}
        </main>
        {toast}
      </div>
      <MobileEventSheet
        code={params.get("event")}
        open={mobile && !!params.get("event")}
        onClose={() => closeOverlay("event")}
      />
      {params.get("reel") && reel?.authorUserId === params.get("reel") && (
        <StoryViewer
          key={`${reel.authorUserId}:${reelTap}`}
          reels={[reel]}
          startUserId={reel.authorUserId}
          doorRect={doorRect}
          onClose={() => closeOverlay("reel")}
        />
      )}
    </PageShell>
  );
}
